import React from 'react'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {formatDateTime, formatPrice} from '../../../../../Common/Formatter'
import {FlexContainer} from '../../../Atoms/Containers'
import {PAYMENT} from '../../../../../Common/Constants'
import {MBContentBodyTypography, MBContentNoteWithMarkTypography, MBContentSubTitleTypography} from '../../../Atoms/Typographies'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {ContentBlock} from '../../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../../Molecules/BaseContentBlocks/ContentTitle'
import {Optional} from '../../../../../Common/TypeHelper'
import {ContractMethod, displayCardNumber, DisplayCreditCardExpireDate, findSelectedCardExpireDate, PaymentInputDetailWithMethod, UsingMyCreditCardInfo} from '../../../../Props/ContractInputInfo'
import {convenienceStoreKeyToName} from '../InputContentBlocks/ConvenienceStoreInputComponent'
import dayjs from 'dayjs'
import {PIMStack} from '../../../Atoms/Layouts/Bases/PIMStack'

type ContractPaymentMethodConfirmContentProps = {
  title: string
  amount: number
  method: Optional<ContractMethod>
  methodDetail: MethodDetailProps
  isTakeover?: boolean
}

export const ContractPaymentMethodConfirm:
  React.VFC<ContractPaymentMethodConfirmContentProps> = (
  {
    title,
    amount,
    method,
    methodDetail,
    isTakeover,
  },
) => {
  const isEditContractToCvs = method === 'convenience_store'
    && methodDetail.paymentInputDetailWithMethod.convenienceStore.convenienceStoreKey === ''

  return (
    <ContentBlock data-testid="block-contract-payment-method-confirm">
      <ContentTitle noMarginBottom={true}>{title}</ContentTitle>
      <Spacer mt={'18px'} mb={'10px'}>
        <MBContentSubTitleTypography>お支払い金額</MBContentSubTitleTypography>
      </Spacer>
      <Spacer mb={'18px'}>
        <MBContentBodyTypography>{`${formatPrice(amount)}円(税込)`}</MBContentBodyTypography>
      </Spacer>
      <MBContentBodyDivider/>
      <Spacer mt={'18px'} mb={'10px'}>
        <MBContentSubTitleTypography>お支払い方法</MBContentSubTitleTypography>
      </Spacer>
      <Spacer mb={'18px'}>
        {
          method &&
            <MBContentBodyTypography>
              {PAYMENT.METHODS_FOR_RADIO_LABEL[method]}
            </MBContentBodyTypography>
        }
        {
          !isEditContractToCvs &&
            <FlexContainer alignItems={'baseline'}>
                <MethodDetail
                    paymentInputDetailWithMethod={methodDetail.paymentInputDetailWithMethod}
                    myCreditCardInfoList={methodDetail.myCreditCardInfoList}
                    maskedCardNo={methodDetail.maskedCardNo}
                />
            </FlexContainer>
        }
      </Spacer>
      {(method === 'convenience_store' && !isEditContractToCvs)
        && <CvsDetail isTakeover={isTakeover}/>}
      <MBContentBodyDivider/>
    </ContentBlock>
  )
}

export type MethodDetailProps = {
  paymentInputDetailWithMethod: PaymentInputDetailWithMethod,
  myCreditCardInfoList: UsingMyCreditCardInfo[],
  maskedCardNo?: string,
}

const MethodDetail: React.VFC<MethodDetailProps> = (props) => {
  const {method, creditCard, mobileCarrier, convenienceStore} = props.paymentInputDetailWithMethod
  switch (method) {
    case 'credit_card':
      return (
        <>
          <MBContentBodyTypography>{
            displayCardNumber(
              creditCard.myCreditCardInfo.selectedCardId,
              props.myCreditCardInfoList,
              props.maskedCardNo,
            )
          }</MBContentBodyTypography>
          <DisplayCreditCardExpireDate expireDate={
            findSelectedCardExpireDate(
              creditCard.creditCardType,
              creditCard.newCreditCardInfo.expireMonth +
              '/' + creditCard.newCreditCardInfo.expireYear,
              creditCard.myCreditCardInfo.selectedCardId,
              props.myCreditCardInfoList,
            )}/>
        </>
      )
    case 'mobile_carrier':
      return (<>
        <MBContentBodyTypography>決済キャリア：{mobileCarrier.carrierKey}</MBContentBodyTypography>
      </>)
    case 'convenience_store':
      return (<MBContentBodyTypography>
        お支払い先コンビニ：{convenienceStoreKeyToName(convenienceStore.convenienceStoreKey)}
      </MBContentBodyTypography>)
    default:
      return (<></>)
  }
}

export type CvsDetailProps = { isTakeover?: boolean }
const CvsDetail: React.VFC<CvsDetailProps> = (props) => {
  const paymentDue = calculatePaymentDue(props.isTakeover)

  return (<>
    <MBContentBodyDivider/>
    <PIMStack spacing={'10px'}>
      <Spacer mt={'18px'}>
        <MBContentSubTitleTypography>お支払い期限</MBContentSubTitleTypography>
      </Spacer>
      <MBContentBodyTypography>{formatDateTime(paymentDue)}</MBContentBodyTypography>
      <MBContentNoteWithMarkTypography note={
        'お申し込みの時間によってはお支払い期限が変わる場合がありますので、実際のお支払い期限は登録完了画面でご確認ください。'
      }/>
    </PIMStack>
    <Spacer mb={'18px'}/>
  </>)
}
const calculatePaymentDue = (isTakeover?: boolean): dayjs.Dayjs => {
  // この値はサーバ側の GmoApiClientForConvenienceStore.DEFAULT_PAYMENT_TERM_DAY と協調して修正する
  const DEFAULT_PAYMENT_TERM_DAY = 2

  const now = dayjs()
  const termDaysLater
    = now.add(DEFAULT_PAYMENT_TERM_DAY, 'd').set('h', 23).set('m', 59)

  return (isTakeover && now.month() !== termDaysLater.month())
    ? now.endOf('month').set('h', 23).set('m', 59)
    : termDaysLater
}
